import React from 'react';

// In-app building blocks in the landing page's style (slate surfaces, uppercase eyebrows,
// primary accents), with a light-mode equivalent of each dark surface.

// Full-bleed page surface inside <Layout> (cancels its padding and grey background)
// (wide: use the full width instead of a centred column)
export const AppPage = ({ children, className = '', wide = false }) => (
  <div className={`-m-3 min-h-[calc(100vh-64px)] bg-slate-50 -mb-24 px-3 pb-28 pt-5 text-slate-600 sm:-m-6 sm:px-6 sm:py-8 dark:bg-slate-950 dark:text-gray-300 ${className}`}>
    <div className={wide ? '' : 'mx-auto max-w-6xl'}>{children}</div>
  </div>
);

export const CARD = 'rounded-2xl border border-slate-200 bg-white dark:border-white/10 dark:bg-white/[0.03]';
export const CARD_FEATURED = 'rounded-2xl border border-primary-400 bg-primary-50/70 dark:border-primary-500/60 dark:bg-primary-600/10';
export const BTN_PRIMARY = 'inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-500 disabled:cursor-not-allowed disabled:opacity-50';
export const BTN_SECONDARY = 'inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-900 transition-colors hover:bg-slate-100 dark:border-white/20 dark:text-white dark:hover:bg-white/10';

export const BTN_DANGER = 'inline-flex items-center justify-center gap-2 rounded-lg bg-rose-600 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-rose-500 disabled:cursor-not-allowed disabled:opacity-50';
export const INPUT = 'block w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-white/15 dark:bg-white/[0.04] dark:text-white dark:placeholder-gray-500 [&>option]:text-slate-900';

export const Eyebrow = ({ children, className = '' }) => (
  <p className={`text-xs font-semibold uppercase tracking-widest text-primary-600 dark:text-primary-400 ${className}`}>{children}</p>
);

// A labelled form control (children is the input)
export const Field = ({ label, hint, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-gray-300">{label}</span>
    {children}
    {hint && <span className="mt-1.5 block text-xs text-slate-500 dark:text-gray-500">{hint}</span>}
  </label>
);

// Page title row: eyebrow, title, optional subtitle and action
export const PageHeader = ({ eyebrow, title, children, action }) => (
  <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
    <div>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h1 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl dark:text-white">{title}</h1>
      {children && <p className="mt-1 text-slate-500 dark:text-gray-400">{children}</p>}
    </div>
    {action}
  </div>
);

// Status pill; tone is one of the keys below
const TONES = {
  blue: 'bg-primary-500/10 text-primary-700 dark:text-primary-300',
  green: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  amber: 'bg-amber-500/10 text-amber-700 dark:text-amber-300',
  red: 'bg-rose-500/10 text-rose-700 dark:text-rose-300',
  gray: 'bg-slate-500/10 text-slate-600 dark:text-gray-400',
};
export const Pill = ({ tone = 'gray', children, className = '' }) => (
  <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${TONES[tone]} ${className}`}>{children}</span>
);

// Empty state inside a card
export const Empty = ({ icon: Icon, title, children, action }) => (
  <div className="flex flex-col items-center px-6 py-14 text-center">
    {Icon && <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-600/10"><Icon className="h-6 w-6 text-primary-600 dark:text-primary-300" /></span>}
    <p className="mt-4 font-semibold text-slate-900 dark:text-white">{title}</p>
    {children && <p className="mt-1 max-w-sm text-sm text-slate-500 dark:text-gray-400">{children}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const SectionTitle = ({ eyebrow, title, children, action }) => (
  <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
    <div>
      {eyebrow && <Eyebrow className="mb-2">{eyebrow}</Eyebrow>}
      <h2 className="text-xl font-semibold tracking-tight text-slate-900 md:text-2xl dark:text-white">{title}</h2>
      {children && <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">{children}</p>}
    </div>
    {action}
  </div>
);

// A pill switcher; options are [{ value, label }]
export const Segmented = ({ options, value, onChange, label, size = 'sm' }) => (
  <div role="group" aria-label={label} className="inline-flex max-w-full overflow-x-auto rounded-xl border border-slate-200 bg-white p-1 dark:border-white/10 dark:bg-white/[0.03]">
    {options.map((o) => (
      <button
        key={o.value}
        onClick={() => onChange(o.value)}
        aria-pressed={value === o.value}
        className={`flex-shrink-0 whitespace-nowrap rounded-lg font-semibold transition-colors ${size === 'sm' ? 'px-3 py-1 text-xs' : 'px-4 py-2 text-sm'} ${value === o.value
          ? 'bg-primary-600 text-white'
          : 'text-slate-500 hover:text-slate-900 dark:text-gray-400 dark:hover:text-white'}`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

// Dark image banner (always dark, so its text reads the same in both themes)
export const HeroPanel = ({ image, children, className = '' }) => (
  <section className={`relative overflow-hidden rounded-3xl border border-slate-200 bg-slate-950 dark:border-white/10 ${className}`}>
    {image && (
      <img
        src={image}
        alt=""
        className="absolute inset-0 h-full w-full object-cover object-[65%_center]"
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
    )}
    <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/75 to-slate-950/25" />
    <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-primary-600/25 blur-3xl" />
    <div className="relative px-5 py-6 sm:px-8 md:py-8">{children}</div>
  </section>
);
